import { caretDownIcon } from "../elements/icons/index.js";
import { eventManager } from "@foretagsplatsen/yaem";
import { getResult } from "../helpers/functional-util.js";
import { widget } from "@foretagsplatsen/widgetjs";
import PerfectScrollbar from "perfect-scrollbar";
import responsive from "../responsive.js";

/**
 *
 * `navigationWidget` is the widget representing the left-hand side menu dedicated
 * to navigation. It is managing some part of the responsiveness behavior.
 *
 * @param {object} spec - Parameter object
 * @param {boolean} spec.visible - Boolean to toggle the widget visibility
 * @param {boolean} [spec.pdsCompliant] - Whether the menu should be PDS styled
 * @param {object} my - protected fields holder
 * @return {*}
 */
function navigationWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);
	let activeItem;

	my.visible = spec.visible || false;

	that.onVisibilityChange = my.events.createEvent();
	that.onIconModeToggled = my.events.createEvent();
	that.items = [];

	my.navigationItems = spec.navigationItems;
	my.pdsCompliant = spec.pdsCompliant || false;

	my.routesWithPotentialMarketingOverlay =
		spec.routesWithPotentialMarketingOverlay;

	let container;

	eventManager.at("app").register("updateLanguage", that.update);

	// Make sure that all items have a menuId.
	my.navigationItems.forEach((item) => {
		ensureMenuId(item);
	});

	my.isIconMode = true;

	that.toggleIconMode = function () {
		// eslint-disable-next-line no-shadow -- bad practice, we should fix this
		const container = that.asJQuery().find(".leftmenu-container");
		my.isIconMode = !my.isIconMode;
		that.onVisibilityChange.trigger(my.isIconMode);
		container.toggleClass("lm-small");
		my.perfectScrollbar && my.perfectScrollbar.update();
		that.onIconModeToggled.trigger(my.isIconMode);
	};

	that.setIconMode = function () {
		if (my.isIconMode) {
			return;
		}
		that.toggleIconMode();
	};

	that.isIconMode = function () {
		return my.isIconMode;
	};

	that.activate = function (id) {
		activeItem = id;
		that.update();
	};

	that.renderContentOn = function (html) {
		if (my.visible) {
			return;
		}

		container = html.div(
			{ class: [my.cssClass(), { "lm-small": my.isIconMode }] },
			html.ul(
				{ class: "leftmenu" },
				my.navigationItems
					.filter(my.isItemVisible)
					.map(my.itemRenderer),
			),
		);

		my.checkForTouchDevice(container);
	};

	my.renderVisibleNavigationItemsOn = function (html) {
		html.render(
			my.navigationItems.filter(my.isItemVisible).map(my.itemRenderer),
		);
	};

	my.didAttach = function () {
		if (!container) {
			return;
		}
		my.perfectScrollbar = new PerfectScrollbar(container.element);
	};

	my.cssClass = function () {
		return ["leftmenu-container", { "pds-menu": my.pdsCompliant }];
	};

	my.isCurrentItem = function (item) {
		if (activeItem === undefined) {
			return false;
		}

		if (item.menuIds && item.menuIds.indexOf(activeItem) !== -1) {
			return true;
		}

		if (
			activeItem === item.menuId ||
			(item.linkTo && item.linkTo.indexOf(`${activeItem}/`) === 0)
		) {
			return true;
		}
		if (!item.children) {
			return false;
		}
		return item.children.some((child) => child.menuId === activeItem);
	};

	my.cssClassForItem = function (item) {
		return [
			"lm-li-item",
			{ current: my.isCurrentItem(item) },
			{ "lm-li-parent": item.children },
			{ highlighted: item.highlighted?.() },
		];
	};

	my.itemRenderer = function (item) {
		return function (html) {
			html.li(
				{
					klass: my.cssClassForItem(item),
					"data-test": item.dataTestName,
				},
				my.itemLinkRenderer(item),
				my.childrenRenderer(item),
			);
		};
	};

	my.isItemVisible = function (item) {
		return my.navigationIsItemVisible(item);
	};

	my.navigationIsItemVisible = function (item) {
		return !item.visible || getResult(item.visible);
	};

	my.itemLinkRenderer = function (item) {
		if (
			my.routesWithPotentialMarketingOverlay &&
			my.routesWithPotentialMarketingOverlay.includes(item.menuId)
		) {
			item.cssClass += " disabled";
		}

		return function (html) {
			const anchor = html
				.a(
					{
						href: my.urlForItem(item),
						klass: [
							item.cssClass,
							"lm-li-item",
							{ current: my.isCurrentItem(item) },
						],
					},
					html.span(item.label, my.caretRenderer(item)),
				)
				.click(() => {
					if (item.children) {
						return;
					}
					item.click?.();
					hideNavigationOnMobileOrIPad();
				});
			if (item.children) {
				anchor.click((event) => {
					event.preventDefault();
					my.expandChildren(anchor);
				});

				that.onVisibilityChange.register((visible) => {
					if (!visible) {
						return;
					}

					my.collapseChildren(anchor);
				});
			}
		};
	};

	my.collapseChildren = function (anchor) {
		const anchorNode = anchor.asJQuery();
		const parent = anchorNode.parent();

		if (!parent.hasClass("active")) {
			return;
		}

		parent.toggleClass("active");

		anchorNode.find("i").toggleClass("fa-caret-down fa-caret-up");
	};

	my.urlForItem = function (item) {
		return item.linkToUrl || (item.linkTo && my.linkTo(item.linkTo, {}));
	};

	my.expandChildren = function (anchor) {
		if (my.isIconMode) {
			return;
		}
		const anchorNode = anchor.asJQuery();

		const parent = anchorNode.parent();
		if (!parent.hasClass("active")) {
			parent.parent().find(".active").removeClass("active");
		}
		parent.toggleClass("active");
		anchorNode.find("i").toggleClass("fa-caret-down fa-caret-up");
	};

	my.caretRenderer = function (item) {
		if (!item.children) {
			return () => {};
		}

		return caretDownIcon;
	};

	my.childrenRenderer = function (item) {
		if (!my.isItemVisible(item)) {
			return function () {};
		}
		return function (html) {
			if (!item.children) {
				return;
			}

			html.ul(
				{ class: "lm-sub-menu" },
				item.children.filter(my.isItemVisible).map(my.subItemRenderer),
			);
		};
	};

	my.subItemRenderer = function (item) {
		return function (html) {
			html.li(
				{
					klass: [my.cssClassForItem(item), "lm-li-sub-item"],
					"data-test": item.dataTestName,
				},
				my.itemLinkRenderer(item),
			);
		};
	};

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	my.checkForTouchDevice = function (container) {
		if (!responsive.isTouchDevice()) {
			return;
		}
		container.asJQuery().addClass("touch-device");
	};

	function hideNavigationOnMobileOrIPad() {
		if (!responsive.isMobileOrIPadPortaitBreakpoint()) {
			return;
		}
		that.setIconMode();
	}

	function ensureMenuId(item) {
		item.menuId ||= item.linkTo;
		if (item.children) {
			item.children.forEach(ensureMenuId);
		}
	}

	return that;
}

export default navigationWidget;
